import { PortletStoreApi } from '../App';
import type {
  CallStatusMessageListener,
  CallStrategy,
  CallStrategyConfig
} from './callstrategy';
import type { at } from '../generated/Beans';
import { intl, NotificationType, notify } from 'ehs-react';
import type {
  CallMenuInterface,
  ScreenSharingOnListener
} from 'virtual-visit-common';
import { CallMenuStoreApi } from 'virtual-visit-common';
type SocketDataBean = at.sense.portlets.saf.virtualvisitpatient.model.SocketDataBean;
type JitsiSocketDataBeanConfig = at.sense.portlets.saf.virtualvisitpatient.model.JitsiSocketDataBeanConfig;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const JitsiMeetExternalAPI: any;

export class JistiCallStrategy implements CallStrategy {
  onScreenShareUpdate(_lst: ScreenSharingOnListener): void {
    throw new Error('Method not implemented.');
  }
  stopScreenSharing(): Promise<void> {
    throw new Error('Method not implemented.');
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  jitsiApi: any;
  preventPhysicianLeftNotification = false;

  callEnded = false;

  getCallStrategyConfig(data: SocketDataBean): CallStrategyConfig {
    return data.jitsiSocketDataBeanConfig;
  }

  async initProviderAndStartCall(
    config: CallStrategyConfig,
    onReadyToClose?: () => void,
    physicianLeftMessageDelay?: number
  ): Promise<void> {
    const jitsiServerUrl = PortletStoreApi.getState().configuration.jitsiServer;
    const url = !jitsiServerUrl ? 'meet.jit.si' : new URL(jitsiServerUrl).host;

    console.log('init Jitsi', config);

    const showPhysicianLeftNoticiation = () => {
      notify({
        message: intl('virtualvisitpatientportlet.view.call.physicianleft'),
        onClose: () => {
          //nothing to do
        },
        title: intl('common.note'),
        type: NotificationType.ERROR
      });
    };

    const jitsiConfg = config as JitsiSocketDataBeanConfig;
    const roomName = jitsiConfg.roomName;
    const jwtToken = jitsiConfg.jwtToken;
    this.initJitsi(
      url,
      roomName,
      jwtToken,
      ' ',
      showPhysicianLeftNoticiation,
      onReadyToClose,
      physicianLeftMessageDelay
    );
  }

  initJitsi(
    url: string,
    roomName: string,
    jwtToken: string,
    patientName?: string,
    showPhysicianLeftNoticiation?: () => void,
    onReadyToClose?: () => void,
    physicianLeftMessageDelay?: number
  ): void {
    console.log('initJitsi', url, roomName, jwtToken, patientName);

    const {
      preselectedCamera,
      preselectedMicrophone,
      preselectedSpeaker
    } = CallMenuStoreApi.getState();

    const options = {
      roomName: roomName,
      devices: {
        audioInput: preselectedMicrophone?.name,
        audioOutput: preselectedSpeaker?.name,
        videoInput: preselectedCamera?.name
      },
      configOverwrite: {
        prejoinPageEnabled: false,
        fileRecordingsEnabled: false,
        liveStreamingEnabled: false,
        hideLobbyButton: false,
        enableCalendarIntegration: false,
        enableAutomaticUrlCopy: false,
        localRecording: {
          enabled: false
        },
        doNotStoreRoom: true,
        remoteVideoMenu: {
          disableKick: true
        },
        disableRemoteMute: true,
        hideParticipantsStats: true,
        hideConferenceSubject: true,
        enableUserRolesBasedOnToken: true,
        disableDeepLinking: true,
        disableFilmstripAutohiding: true,
        disableBeforeUnloadHandlers: true,
        toolbarButtons: [
          'microphone',
          'camera',
          'desktop',
          'select-background',
          'hangup'
        ],
        disableModeratorIndicator: true
      },

      interfaceConfigOverwrite: {
        VIDEO_QUALITY_LABEL_DISABLED: true,
        SHOW_CHROME_EXTENSION_BANNER: false
      },

      jwt: jwtToken,
      parentNode: document.getElementById('jitsi-video-conference'),
      userInfo: {
        displayName: patientName
      },
      onload: () => {
        console.log('Jitsi loaded');
        /*
        workaround: if the iframe width < 500px the filmstrip with self preview is not shown
        and there is no config option to change this behavior.
        disableFilmstripAutohiding -> true only prevents the filmstrip from hiding when resizing the window
         */
        const jv = document.getElementById('jitsi-video-conference');
        const jitsiFrame = jv?.getElementsByTagName('iframe')[0];
        console.log('jitsi iframe width', jitsiFrame?.clientWidth, jitsiFrame);
        if ((jitsiFrame?.clientWidth || 0) < 500) {
          this.jitsiApi.executeCommand('toggleFilmStrip');
        }
      }
    };

    console.log('jitsi options', options);

    this.jitsiApi = new JitsiMeetExternalAPI(url, options);

    this.jitsiApi.addListener('readyToClose', () => {
      //this listener is called after the patient clicked on the hangup button
      console.log('jitsi readyToClose');
      this.callEnded = true;
      this.jitsiApi.dispose();
      if (onReadyToClose) {
        onReadyToClose();
      }
    });

    if (showPhysicianLeftNoticiation) {
      this.checkForParticipantLeaving(
        showPhysicianLeftNoticiation,
        physicianLeftMessageDelay
      );
    }
  }

  checkForParticipantLeaving(
    showPhysicianLeftNoticiation: () => void,
    physicianLeftMessageDelay?: number
  ): void {
    console.log('checkForParticipantLeaving');
    this.jitsiApi.addEventListener('participantLeft', () => {
      console.debug('@@@VVOP-> Participant left');
      setTimeout(() => {
        if (this.callEnded) {
          return;
        }
        if (this.preventPhysicianLeftNotification) {
          this.preventPhysicianLeftNotification = false;
        } else {
          showPhysicianLeftNoticiation();
        }
      }, physicianLeftMessageDelay || 5000);
    });
  }

  closeJistiCall(): void {
    this.jitsiApi.executeCommand('hangup');
    this.jitsiApi.dispose();
  }

  stopCall(): Promise<void> {
    this.closeJistiCall();
    return Promise.resolve();
  }

  setPreventPhysicianLeftNotification(prevent: boolean): void {
    this.preventPhysicianLeftNotification = prevent;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  addCallStatusMessageListener(lst: CallStatusMessageListener): void {
    //noting to do here
  }

  public getCallMenuInterface(): CallMenuInterface | undefined {
    return undefined;
  }
}
